import { Label } from '../ui/label';
import { useForm } from 'react-hook-form';
import StyledInput from '../styled/StyledInput';
import { useCallback, useState } from 'react';
import { CircuitDayImportSchema } from '@/types/circuits/CircuitDayImportFormData';
import { CircuitDayImportFormData } from '@/types/circuits/CircuitDayImportFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import StyledButton from '../styled/StyledButton';
import { useOnImportCircuitDay } from '@/hooks/useCircuitImport';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import Modal from '../ui/Modal';
import { useTranslation } from '@/i18n';

interface CircuitEditorialDayImportProps {
  index: number;
  daysLength: number;
  addBenefits: (props: {
    standardBenefit: BenefitFormData;
    optionalBenefits: BenefitFormData[];
    type: CircuitBenefitType;
    stepIndex: number;
    stepNbDays?: number;
    stepId: string;
    isModification: boolean;
  }) => void;
  setDisplayedDayIndex: React.Dispatch<React.SetStateAction<number>>;
}

const CircuitEditorialDayImport = ({
  index,
  daysLength,
  addBenefits,
  setDisplayedDayIndex,
}: CircuitEditorialDayImportProps) => {
  const { t } = useTranslation();
  const {
    control: circuitDayImportControl,
    handleSubmit,
    formState: {
      isValid: isCircuitDayImportValid,
      isDirty: isCircuitDayImportDirty,
    },
  } = useForm<CircuitDayImportFormData>({
    resolver: zodResolver(CircuitDayImportSchema),
    defaultValues: {
      circuitDayRef: '',
    },
    mode: 'onSubmit',
  });

  const { onImportCircuitDay, isPending } = useOnImportCircuitDay({
    index,
    daysLength,
    addBenefits,
    setDisplayedDayIndex,
  });

  const [circuitCode, setCircuitCode] = useState<number | null>(null);
  const [dayNumber, setDayNumber] = useState<number | null>(null);

  const onSubmit = useCallback(async (data: CircuitDayImportFormData) => {
    if (!data.circuitDayRef) return;

    const circuitCode = parseInt(
      data.circuitDayRef.replace(/^C(\d+)J\d+$/, '$1'),
    );
    const dayNumber = parseInt(
      data.circuitDayRef.replace(/^C\d+J(\d+)$/, '$1'),
    );

    setCircuitCode(circuitCode);
    setDayNumber(dayNumber);
  }, []);

  return (
    <>
      <Label className="text-base">
        {t('circuits.editorial.dayImport.importStep')}
      </Label>
      <form onSubmit={handleSubmit(onSubmit)} className="w-fit">
        <div className="max-w-56 flex flex-col gap-2">
          <StyledInput
            control={circuitDayImportControl}
            name="circuitDayRef"
            title={t('circuits.editorial.dayImport.stepReference')}
            placeholder={t(
              'circuits.editorial.dayImport.stepReferencePlaceholder',
            )}
          />
          <StyledButton
            type="submit"
            className="w-full"
            title={t('circuits.editorial.dayImport.import')}
            disabled={
              !isCircuitDayImportValid || !isCircuitDayImportDirty || isPending
            }
          />
        </div>
      </form>
      {circuitCode && dayNumber && (
        <AddStepModal
          closeModal={() => {
            setCircuitCode(null);
            setDayNumber(null);
          }}
          actionOnClick={onImportCircuitDay}
          circuitCode={circuitCode}
          dayNumber={dayNumber}
        />
      )}
    </>
  );
};

export default CircuitEditorialDayImport;

interface AddStepModalProps {
  closeModal: () => void;
  actionOnClick: (circuitCode: number, dayNumber: number) => void;
  circuitCode: number;
  dayNumber: number;
}

const AddStepModal = ({
  closeModal,
  actionOnClick,
  circuitCode,
  dayNumber,
}: AddStepModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={t('circuits.editorial.dayImport.importStepTitle')}
      closeModal={closeModal}
      actionTitle={t('circuits.editorial.dayImport.import')}
      actionOnClick={() => actionOnClick(circuitCode, dayNumber)}
      message={t('circuits.editorial.dayImport.importStepMessage')}
    />
  );
};
